<script>
  import { onMount } from "svelte";

  // --- ゲームの状態管理 ---
  let allProductFiles = [];
  let quizFilePaths = [];
  let currentQuestionIndex = -1;
  let currentQuestion = null;
  let revealedKeywords = [];
  let userChoice = null;
  let score = 0;
  const QUIZ_LENGTH = 5;
  let maxScore = QUIZ_LENGTH * 10;

  // 'loading', 'start', 'playing', 'answered', 'end', 'error'
  let gameState = "loading";

  let feedbackMessage = "";
  let keywordTimer = null;
  const KEYWORD_REVEAL_INTERVAL = 2000;

  // --- UI 関連 ---
  $: keywordScale =
    revealedKeywords && revealedKeywords.length > 0
      ? Math.max(0.5, Math.min(1, 6 / revealedKeywords.length))
      : 1;
  $: keywordFontSize = (1.1 * keywordScale).toFixed(3) + "rem";
  $: keywordPaddingV = Math.max(4, Math.round(8 * keywordScale)) + "px";
  $: keywordPaddingH = Math.max(6, Math.round(15 * keywordScale)) + "px";

  // --- 結果 ---
  let results = [];

  // --- 初期化 ---
  onMount(async () => {
    try {
      const response = await fetch("./manifest.json");
      allProductFiles = await response.json();
      if (allProductFiles.length >= 4) { // 正解1 + 選択肢3
        gameState = "start";
      } else {
        gameState = "error";
      }
    } catch (e) {
      console.error("Failed to load manifest.json:", e);
      gameState = "error";
    }
  });

  function shuffleArray(array) {
    let newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
  }

  function startGame() {
    const shuffledFiles = shuffleArray(allProductFiles);
    quizFilePaths = shuffledFiles.slice(0, Math.min(QUIZ_LENGTH, shuffledFiles.length));
    
    score = 0;
    currentQuestionIndex = -1;
    results = [];
    
    nextQuestion();
    gameState = "playing";
  }

  async function nextQuestion() {
    clearTimeout(keywordTimer);
    revealedKeywords = [];
    userChoice = null;
    feedbackMessage = "";
    currentQuestion = null; // 質問をリセット
    currentQuestionIndex++;

    if (currentQuestionIndex >= quizFilePaths.length) {
      gameState = "end";
      return;
    }

    const correctAnswerPath = quizFilePaths[currentQuestionIndex];
    
    // 不正解の選択肢用のファイルをランダムに3つ選ぶ
    const distractorPaths = shuffleArray(allProductFiles.filter(p => p !== correctAnswerPath)).slice(0, 3);

    try {
      const pathsToFetch = [correctAnswerPath, ...distractorPaths];
      const responses = await Promise.all(pathsToFetch.map(path => fetch(path)));
      const jsonData = await Promise.all(responses.map(res => res.json()));

      // 取得したJSONをクイズ用の形式に変換
      const allOptions = jsonData.map(data => ({
        title: data.product_name,
        // keywordsは {word: '原材料名'} の形式に変換
        keywords: (data.ingredients || data.ingredients_list || []).map(ing => ({ word: ing })),
        url: data.url
      }));
      
      const correctAnswer = allOptions[0];
      const options = shuffleArray(allOptions);

      currentQuestion = {
        correctAnswer,
        keywordsToShow: [...correctAnswer.keywords].reverse(), // 後ろから表示するため逆順に
        options,
      };

      gameState = "playing";
      setTimeout(() => {
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }
      }, 50);
      revealNextKeyword();

    } catch (e) {
      console.error("Failed to load question data:", e);
      gameState = "error";
    }
  }

  function revealNextKeyword() {
    if (gameState !== "playing" || !currentQuestion) return;
    if (revealedKeywords.length < currentQuestion.keywordsToShow.length) {
      revealedKeywords = [
        ...revealedKeywords,
        currentQuestion.keywordsToShow[revealedKeywords.length],
      ];
      keywordTimer = setTimeout(revealNextKeyword, KEYWORD_REVEAL_INTERVAL);
    }
  }

  function handleAnswer(chosenOption) {
    if (gameState !== "playing") return;
    clearTimeout(keywordTimer);
    gameState = "answered";
    userChoice = chosenOption.title;
    const isCorrect = userChoice === currentQuestion.correctAnswer.title;

    if (isCorrect) {
      const k = revealedKeywords.length;
      const total_k = currentQuestion.correctAnswer.keywords.length;
      let pointsEarned = 10;
      if (total_k > 1) {
        pointsEarned = Math.round(10 - 9 * (k - 1) / (total_k - 1));
      }
      pointsEarned = Math.max(1, pointsEarned); // Ensure minimum score of 1

      score += pointsEarned;
      feedbackMessage = `${pointsEarned}点獲得！ 正解！🎉`;
    } else {
      feedbackMessage = `不正解... 正解は「${currentQuestion.correctAnswer.title}」でした。`;
    }

    results = [
      ...results,
      {
        correct: isCorrect,
        title: currentQuestion.correctAnswer.title,
        url: currentQuestion.correctAnswer.url ?? "",
        keywords: currentQuestion.correctAnswer.keywords.map((k) => k.word),
      },
    ];

    setTimeout(nextQuestion, 3000);

    try {
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
    } catch (e) {
      // noop
    }
  }

  function getButtonClass(option) {
    if (gameState !== "answered") return "option-btn";
    const isCorrectAnswer = option.title === currentQuestion.correctAnswer.title;
    if (isCorrectAnswer) return "option-btn correct";
    const isChosenIncorrectly = option.title === userChoice;
    if (isChosenIncorrectly) return "option-btn incorrect";
    return "option-btn";
  }
</script>

<main>
  <h1>食品原材料クイズ 👨‍🍳</h1>

  {#if gameState === "loading"}
    <p>データを読み込んでいます...</p>
  {:else if gameState === "error"}
    <div class="card error-card">
      <p>エラー: クイズデータの読み込みに失敗しました。</p>
      <p>public/manifest.json と public/products_data を確認してください。</p>
    </div>
  {:else if gameState === "start"}
    <div class="card start-screen">
      <h2>クイズ (全{QUIZ_LENGTH}問)</h2>
      <div class="rules">
        <h3>得点ルール</h3>
        <ul>
          <li>
            最初のヒントで正解すると<strong>10点</strong>です。
          </li>
          <li>
            ヒントが1つ増えるごとに、その問題の全原材料数に応じて点数が減っていき、最後のヒントで正解すると<strong>1点</strong>になります。
          </li>
        </ul>
      </div>
      <button on:click={startGame}>スタート</button>
    </div>
  {:else if gameState === "playing" || gameState === "answered"}
    {#if currentQuestion}
      <div class="status-bar">
        <span>スコア: {score}点</span>
        <span>{currentQuestionIndex + 1} / {quizFilePaths.length}問目</span>
      </div>

      <div class="card question-card">
        {#if gameState === "answered"}
          <div class="feedback {userChoice === currentQuestion.correctAnswer.title ? 'feedback-correct' : 'feedback-incorrect'}">
            {@html feedbackMessage}
          </div>
        {:else}
          <h2>この食品なあに？</h2>
        {/if}

        <div class="keywords-container">
          {#if revealedKeywords.length === 0}
            <span class="keyword-placeholder">最初のヒントを待っています...</span>
          {:else}
            {#each revealedKeywords as kw (kw.word)}
              <span class="keyword" style="font-size: {keywordFontSize}; padding: {keywordPaddingV} {keywordPaddingH};">
                {kw.word}
              </span>
            {/each}
          {/if}
        </div>
      </div>

      {#key currentQuestionIndex}
        <div class="options-grid">
          {#each currentQuestion.options as option (option.title)}
            <button class={getButtonClass(option)} on:click={() => handleAnswer(option)} disabled={gameState === "answered"}>
              {option.title}
            </button>
          {/each}
        </div>
      {/key}
    {:else}
       <div class="card"><p>次の問題を準備中...</p></div>
    {/if}
  {:else if gameState === "end"}
    <div class="card result-card">
      <h2>ゲーム終了！</h2>
      <p class="final-score">最終スコアは: {score} / {maxScore}点 です</p>

      <section class="results-list">
        <h3>各問の結果</h3>
        {#if results.length === 0}
          <p class="muted">結果がありません。</p>
        {:else}
          <ol>
            {#each results as r, idx}
              <li class="result-item">
                <span class="result-mark">{r.correct ? "⭕️" : "❌️"}</span>
                {#if r.url}
                <a class="result-title" href={r.url} target="_blank" rel="noopener noreferrer">{r.title}</a>
                {:else}
                <span class="result-title">{r.title}</span>
                {/if}
                <div class="result-keywords">
                  {#each r.keywords as kw}
                    <span class="result-keyword">{kw}</span>
                  {/each}
                </div>
              </li>
            {/each}
          </ol>
        {/if}
      </section>

      <button on:click={startGame}>もう一度挑戦する</button>
    </div>
  {/if}
</main>

<style>
  :root {
    --bg-color: #f0f4f8;
    --card-bg: #ffffff;
    --text-color: #333;
    --primary-color: #3498db;
    --primary-hover: #2980b9;
    --correct-color: #2ecc71;
    --incorrect-color: #e74c3c;
    --font-family: "Helvetica Neue", Arial, sans-serif;
  }

  main {
    font-family: var(--font-family);
    text-align: center;
    padding: 2em;
    background-color: var(--bg-color);
    min-height: 100vh;
    box-sizing: border-box;
    color: var(--text-color);
  }

  h1 {
    color: var(--primary-color);
    margin-bottom: 2rem;
  }

  .card {
    background: var(--card-bg);
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
    padding: 1.25rem 1.5rem;
    max-width: 820px;
    margin: 0.6rem auto;
  }

  .start-screen .rules {
    background-color: #f9f9f9;
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 0.5rem 1.5rem;
    margin: 1.5rem 0;
    text-align: left;
  }

  .start-screen ul {
    list-style-type: "✅ ";
    padding-left: 1.5rem;
  }
  .start-screen li {
    margin-bottom: 0.75rem;
  }

  .status-bar {
    display: flex;
    justify-content: space-between;
    max-width: 600px;
    margin: 0 auto 1rem;
    font-size: 1.2rem;
    font-weight: bold;
  }

  .keywords-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    min-height: 56px;
    padding: 0.5rem 0.75rem;
    border: 1.5px dashed #ddd;
    border-radius: 10px;
    margin-top: 1rem;
    align-items: center;
  }

  .keyword {
    background-color: #ecf0f1;
    color: #34495e;
    padding: 8px 15px;
    border-radius: 20px;
    font-size: 1.1rem;
    animation: fadeIn 0.5s ease;
    transition:
      font-size 180ms ease,
      padding 180ms ease,
      transform 180ms ease;
  }

  .keyword-placeholder {
    color: #95a5a6;
    font-style: italic;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.8);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .options-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    max-width: 600px;
    margin: 2rem auto 0;
  }

  button {
    padding: 1rem;
    font-size: 1.1rem;
    font-weight: bold;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    background-color: var(--primary-color);
    color: white;
    transition: all 0.2s ease;
  }

  @media (hover: hover) and (pointer: fine) {
    button:hover:not(:disabled) {
      background-color: var(--primary-hover);
      transform: translateY(-2px);
    }
  }

  button {
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  .option-btn {
    background-color: var(--card-bg);
    color: var(--primary-color);
    border: 2px solid var(--primary-color);
  }

  .option-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .option-btn.correct {
    background-color: var(--correct-color);
    border-color: var(--correct-color);
    color: white;
    transform: scale(1.05);
  }

  .option-btn.incorrect {
    background-color: var(--incorrect-color);
    border-color: var(--incorrect-color);
    color: white;
    opacity: 0.8;
  }

  button:focus {
    outline: none;
  }

  .final-score {
    font-size: 1.5rem;
    margin: 2rem 0;
  }

  .results-list {
    text-align: left;
    margin-top: 1rem;
  }

  .results-list h3 {
    margin: 0 0 0.5rem 0;
    color: #1b3d6a;
  }

  .results-list ol {
    padding-left: 1.25rem;
    margin: 0 0 1rem 0;
  }

  .result-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0.5rem 0;
    border-bottom: 1px dashed #eee;
  }

  .result-mark {
    font-size: 1.25rem;
    margin-right: 0.5rem;
  }

  .result-title {
    color: var(--primary-color);
    font-weight: 700;
    text-decoration: none;
  }

  .result-keywords {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .result-keyword {
    background: #f1f5f9;
    color: #333;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.9rem;
  }

  .error-card {
    border-left: 5px solid var(--incorrect-color);
    text-align: left;
  }

  .feedback {
    font-size: 1.5rem;
    font-weight: bold;
    padding: 0.5rem;
    border-radius: 8px;
    animation: fadeIn 0.3s ease;
  }

  .feedback-correct {
    color: var(--correct-color);
  }

  .feedback-incorrect {
    color: var(--incorrect-color);
    font-size: 1.2rem;
  }
</style>